<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="text-center">
        <h1>Meine Gutschriften</h1>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <v-alert type="info" outlined dense border="left" class="mb-0">
          Dieses Log zeigt nur einen Teil der Historie. Falls ein Eintrag hier
          nicht auftaucht, heißt das nicht, dass die entsprechenden Punkte
          nicht gutgeschrieben wurden.
        </v-alert>
      </v-col>
    </v-row>

    <v-row class="mb-4" dense v-if="!$store.state.logged_in">
      <v-col cols="12" class="pt-2">
        <v-btn tile color="#9147ff" class="text--white" :href="loginUrl" :block="$vuetify.breakpoint.smAndDown">
          <v-icon left>mdi-twitch</v-icon>
          Login mit Twitch
        </v-btn>
      </v-col>
    </v-row>

    <template v-else>
      <v-toolbar flat>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Suche"
          single-line
          hide-details
        ></v-text-field>
      </v-toolbar>

      <!-- Desktop: Data Table -->
      <v-data-table
        v-if="!$vuetify.breakpoint.smAndDown"
        :search="search"
        :headers="headers"
        :items="entries"
        sort-by="tmstmp"
        sort-desc
        class="elevation-1"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td class="text-center">{{ new Date(item.tmstmp * 1000).toLocaleString('de-De') }}</td>
            <td class="text-center">
              <span :class="item.amount >= 0 ? 'green--text' : 'red--text'">
                {{ item.amount >= 0 ? '+' : '' }}{{ item.amount.toLocaleString('de-DE') }}
              </span>
            </td>
            <td class="text-center">{{ currencyLabel(item.currency) }}</td>
            <td class="text-center">
              <v-btn
                v-if="item.breakdown && item.breakdown.length"
                icon
                small
                @click="toggleExpand(item)"
              >
                <v-icon>{{ isExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </v-btn>
            </td>
          </tr>
          <tr v-if="isExpanded(item)" class="breakdown-row">
            <td :colspan="headers.length" class="py-4">
              <breakdown-list :entry="item" />
            </td>
          </tr>
        </template>
      </v-data-table>

      <!-- Mobile: Card List -->
      <template v-else>
        <v-card
          v-for="(item, index) in mobileItems"
          :key="index"
          class="mb-2"
          outlined
          :ripple="!!(item.breakdown && item.breakdown.length)"
          @click="openDetail(item)"
        >
          <v-card-text class="d-flex align-center py-3">
            <v-avatar
              size="40"
              :color="item.amount >= 0 ? 'green lighten-4' : 'red lighten-4'"
              class="mr-3 flex-shrink-0"
            >
              <v-icon :color="item.amount >= 0 ? 'green darken-2' : 'red darken-2'">
                {{ item.amount >= 0 ? 'mdi-arrow-up-bold' : 'mdi-arrow-down-bold' }}
              </v-icon>
            </v-avatar>

            <div class="flex-grow-1 text-truncate mr-2">
              <div class="font-weight-medium text-truncate">{{ currencyLabel(item.currency) }}</div>
              <div class="caption grey--text text-truncate">
                {{ new Date(item.tmstmp * 1000).toLocaleString('de-De') }}
              </div>
            </div>

            <span
              class="subtitle-1 font-weight-bold flex-shrink-0"
              :class="item.amount >= 0 ? 'green--text' : 'red--text'"
            >
              {{ item.amount >= 0 ? '+' : '' }}{{ item.amount.toLocaleString('de-DE') }}
            </span>
            <v-icon v-if="item.breakdown && item.breakdown.length" class="ml-1" color="grey">
              mdi-chevron-right
            </v-icon>
          </v-card-text>
        </v-card>
        <v-alert v-if="mobileItems.length === 0" type="info" class="mt-2">
          Noch keine Gutschriften vorhanden.
        </v-alert>

        <v-dialog v-model="detailDialog" max-width="400">
          <v-card v-if="detailItem">
            <v-card-title>Aufschlüsselung</v-card-title>
            <v-card-text>
              <breakdown-list :entry="detailItem" />
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="detailDialog = false">Schließen</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
    </template>
  </v-container>
</template>

<script>
import BackendService from '@/services/BackendService';
import PointCreditBreakdownList from '@/components/PointCreditBreakdownList.vue';

export default {
  name: 'PointCreditLogView',
  components: { BreakdownList: PointCreditBreakdownList },
  data: () => ({
    search: '',
    entries: [],
    socket: null,
    heartbeatInterval: null,
    reconnectTimeout: null,
    suppressReconnect: false,
    nextKey: 0,
    expandedKeys: [],
    detailDialog: false,
    detailItem: null,
    headers: [
      { text: 'Zeitpunkt', value: 'tmstmp', align: 'center' },
      { text: 'Betrag', value: 'amount', align: 'center' },
      { text: 'Währung', value: 'currency', align: 'center' },
      {
        text: '', value: 'expand', align: 'center', sortable: false,
      },
    ],
  }),
  computed: {
    loginUrl() {
      const base = `https://id.twitch.tv/oauth2/authorize?response_type=code&client_id=${process.env.VUE_APP_TWITCH_CLIENT_ID}&redirect_uri=${process.env.VUE_APP_TWITCH_REDIRECT_URL}&scope=user%3Aread%3Aemail`;
      return `${base}&state=point_credit_log|${this.$route.params.id}`;
    },
    mobileItems() {
      const sorted = [...this.entries].sort((a, b) => b.tmstmp - a.tmstmp);
      if (!this.search) return sorted;
      const s = this.search.toLowerCase();
      return sorted.filter((e) => this.currencyLabel(e.currency).toLowerCase().includes(s));
    },
  },
  mounted() {
    this.loadInstance(this.$route.params.id);
  },
  beforeRouteUpdate(to, from, next) {
    this.loadInstance(to.params.id);
    next();
  },
  destroyed() {
    this.teardownSocket();
  },
  methods: {
    loadInstance(id) {
      this.teardownSocket();
      this.entries = [];
      BackendService.loadPointCreditLog({ id }).then((data) => {
        this.entries = data.data.map((entry) => this.withKey(entry));
      }).catch(() => {
        this.entries = [];
      }).finally(() => {
        this.$store.dispatch('refreshStatus');
      });
      this.connectSocket();
    },
    withKey(entry) {
      this.nextKey += 1;
      return { ...entry, rowKey: this.nextKey };
    },
    openDetail(item) {
      if (!item.breakdown || !item.breakdown.length) return;
      this.detailItem = item;
      this.detailDialog = true;
    },
    isExpanded(item) {
      return this.expandedKeys.includes(item.rowKey);
    },
    toggleExpand(item) {
      this.expandedKeys = this.isExpanded(item)
        ? this.expandedKeys.filter((k) => k !== item.rowKey)
        : [item.rowKey];
    },
    currencyLabel(currency) {
      const labels = {
        loyalty_points: this.$store.state.loyaltypoints_name,
        xp: this.$store.state.xp_name,
        monthly_points: this.$store.state.monthlypoints_name,
      };
      return labels[currency] || currency;
    },
    teardownSocket() {
      this.suppressReconnect = true;
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
      if (this.socket) {
        this.socket.close();
        this.socket = null;
      }
    },
    stopHeartbeat() {
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
    },
    startHeartbeat() {
      this.stopHeartbeat();
      this.heartbeatInterval = setInterval(() => {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          this.socket.send('ping');
        }
      }, 10000);
    },
    scheduleReconnect() {
      if (this.reconnectTimeout) return;
      this.reconnectTimeout = setTimeout(() => {
        this.reconnectTimeout = null;
        this.connectSocket();
      }, 2000);
    },
    connectSocket() {
      this.suppressReconnect = false;
      this.socket = new WebSocket(
        `${process.env.VUE_APP_WS_PROTOCOL}://${process.env.VUE_APP_WS_URL}`,
      );

      this.socket.addEventListener('open', () => {
        this.startHeartbeat();
      });

      this.socket.addEventListener('message', (event) => {
        if (event.data === 'pong') {
          return;
        }

        let command = null;
        try {
          command = JSON.parse(event.data);
        } catch (err) {
          console.warn('⚠️ Ungültige Nachricht:', event.data);
          return;
        }

        switch (command.cmd) {
          case 'init':
            this.socket.send(
              JSON.stringify({
                cmd: 'init',
                id: this.$route.params.id,
                clientType: 'trigger',
              }),
            );
            break;
          case 'pointCreditAdd':
            this.entries.unshift(this.withKey(command.data));
            break;
          default:
            break;
        }
      });

      this.socket.addEventListener('error', (err) => {
        console.error('❌ WebSocket Fehler:', err);
        this.socket.close();
      });

      this.socket.addEventListener('close', () => {
        this.stopHeartbeat();
        if (!this.suppressReconnect) {
          this.scheduleReconnect();
        }
      });
    },
  },
};
</script>

<style scoped>
.breakdown-row {
  background-color: rgba(0, 0, 0, 0.03);
}
.breakdown-row:hover {
  background-color: rgba(0, 0, 0, 0.03) !important;
}
</style>
